import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { 
  Settings, 
  Award, 
  Clock, 
  Users, 
  Phone,
  Mail,
  MapPin,
  Calculator,
  Compass,
  Building,
  HardHat,
  Wrench,
  Zap,
  CheckCircle,
  FileText,
  Monitor
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SchemaMarkup from "@/components/SchemaMarkup";
import SEOMeta from "@/components/SEOMeta";

// Import images
import designEngineering from "@/assets/design-engineering.jpg";
import designEngineeringTeam from "@/assets/design-engineering-team.jpg";
import engineeringBlueprints from "@/assets/engineering-blueprints.jpg";
import engineeringConsultation from "@/assets/engineering-consultation.jpg";
import structuralModeling from "@/assets/structural-modeling.jpg";

const DesignEngineeringServices = () => {
  const faqData = [
    {
      question: "What design engineering services do you provide?",
      answer: "We provide comprehensive design engineering services including structural design, CAD drafting, technical drawings, building information modeling (BIM), engineering calculations, and code compliance reviews for all types of construction projects."
    },
    {
      question: "Are your engineers licensed in Ontario?",
      answer: "Yes, all our design engineers are licensed Professional Engineers (P.Eng.) in Ontario with extensive experience in structural, civil, and building engineering disciplines."
    },
    {
      question: "Can you handle both residential and commercial design engineering?",
      answer: "Absolutely. We provide design engineering services for residential homes, commercial buildings, industrial facilities, infrastructure projects, and specialized structures throughout Ontario."
    },
    {
      question: "Do you use modern CAD and BIM software?",
      answer: "Yes, we use the latest CAD software including AutoCAD, Revit, SketchUp, and other industry-standard design tools to create accurate technical drawings and 3D models."
    },
    {
      question: "How long does the design engineering process take?",
      answer: "Project timelines vary based on complexity and scope. Simple residential projects may take 2-4 weeks, while complex commercial projects can take 6-12 weeks. We provide detailed timelines during consultation."
    },
    {
      question: "Do you provide engineering stamps and approvals?",
      answer: "Yes, our licensed Professional Engineers can provide official engineering stamps, seals, and approvals required for building permits and regulatory compliance in Ontario."
    }
  ];

  const serviceData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Design Engineering Services Ontario",
    "description": "Professional design engineering services including structural design, CAD drafting, technical drawings, and engineering consulting by licensed P.Eng. engineers across Ontario.",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Blue Knight Contracting",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Ontario",
        "addressCountry": "CA"
      },
      "telephone": "+1-647-760-8854"
    },
    "areaServed": "Ontario, Canada",
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Design Engineering Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Structural Design",
            "description": "Professional structural engineering design and analysis"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "CAD Drafting",
            "description": "Technical CAD drawings and blueprints"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Engineering Consulting",
            "description": "Professional engineering consultation and analysis"
          }
        }
      ]
    }
  };

  const engineeringServices = [
    {
      title: "Structural Engineering",
      description: "Comprehensive structural design and analysis for buildings, bridges, and specialized structures.",
      icon: Building,
      services: [
        "Structural Design & Analysis",
        "Foundation Engineering",
        "Load Calculations",
        "Seismic & Wind Analysis",
        "Steel & Concrete Design",
        "Structural Inspections"
      ]
    },
    {
      title: "CAD Design & Drafting",
      description: "Professional technical drawings, blueprints, and CAD services using industry-standard software.",
      icon: Monitor,
      services: [
        "2D Technical Drawings",
        "3D Modeling & Visualization",
        "Architectural Plans",
        "Engineering Schematics",
        "As-Built Drawings",
        "Construction Details"
      ]
    },
    {
      title: "Building Information Modeling",
      description: "Advanced BIM services for complex projects with 3D modeling and collaborative design.",
      icon: Settings,
      services: [
        "3D BIM Modeling",
        "Clash Detection",
        "Quantity Takeoffs",
        "4D Scheduling",
        "Collaborative Design",
        "Model Coordination"
      ]
    },
    {
      title: "Engineering Calculations",
      description: "Detailed engineering calculations and analysis for structural and mechanical systems.",
      icon: Calculator,
      services: [
        "Structural Calculations",
        "Load Analysis",
        "Material Specifications",
        "Code Compliance Review",
        "Engineering Reports",
        "Performance Analysis"
      ]
    },
    {
      title: "Design Consultation",
      description: "Expert engineering consultation for design optimization and problem-solving.",
      icon: Users,
      services: [
        "Design Review & Analysis",
        "Value Engineering",
        "Feasibility Studies",
        "Code Consultation",
        "Design Optimization",
        "Technical Advisory"
      ]
    },
    {
      title: "Permit & Approval Services",
      description: "Professional engineering stamps, approvals, and permit application support.",
      icon: CheckCircle,
      services: [
        "Engineering Stamps & Seals",
        "Permit Drawings",
        "Code Compliance",
        "Regulatory Submissions",
        "Municipal Applications",
        "Professional Certifications"
      ]
    }
  ];

  const serviceAreas = [
    "Toronto", "Ottawa", "Hamilton", "London", "Windsor", "Kitchener-Waterloo",
    "Thunder Bay", "Sudbury", "Kingston", "Barrie", "Oshawa", "St. Catharines",
    "Cambridge", "Guelph", "Brantford", "Peterborough", "Sarnia", "Belleville"
  ];

  return (
    <>
      <SEOMeta 
        title="Design Engineering Services Ontario | Licensed P.Eng. & CAD Design"
        description="Professional design engineering services in Ontario. Licensed P.Eng., CAD drafting, structural design & BIM modeling. Serving Toronto, Hamilton, Niagara & GTA construction projects."
        keywords="design engineering Ontario, licensed engineer Toronto, CAD drafting Hamilton, structural design Niagara, BIM modeling GTA, engineering services, P.Eng"
        canonicalUrl="https://blueknightcontracting.ca/services/design-engineering"
      />
      <SchemaMarkup type="Service" data={serviceData} />
      <SchemaMarkup type="FAQ" data={faqData} />
      
      <Header />
      
      <main className="min-h-screen">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-primary via-primary-dark to-secondary py-24 lg:py-32 overflow-hidden">
          <div className="absolute inset-0 bg-black/20"></div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="text-white">
                <Badge variant="secondary" className="mb-4 bg-white/10 text-white border-white/20">
                  Professional Design Engineering
                </Badge>
                <h1 className="text-4xl lg:text-6xl font-bold mb-6 leading-tight">
                  Design Engineering Services Across Ontario
                </h1>
                <p className="text-xl lg:text-2xl mb-8 text-white/90 leading-relaxed">
                  Licensed Professional Engineers providing structural design, CAD drafting, technical drawings, and engineering consulting. From concept to completion with precision and expertise.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 mb-8">
                  <Button size="lg" variant="secondary" className="text-lg px-8" asChild>
                    <Link to="/contact">Get Engineering Quote</Link>
                  </Button>
                  <Button size="lg" variant="outline" className="text-lg px-8 bg-white/10 border-white/30 text-white hover:bg-white/20" asChild>
                    <a href="tel:+16477608854">Call P.Eng.: (647) 760-8854</a>
                  </Button>
                </div>
                <div className="flex flex-wrap gap-6 text-white/80">
                  <div className="flex items-center gap-2">
                    <Award className="h-5 w-5" />
                    <span>Licensed P.Eng. Engineers</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5" />
                    <span>Code Compliant Designs</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="h-5 w-5" />
                    <span>Fast Turnaround</span>
                  </div>
                </div>
              </div>
              <div className="relative">
                <img 
                  src={designEngineering} 
                  alt="Professional design engineering services in Ontario" 
                  className="w-full h-auto rounded-lg shadow-2xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Services Overview */}
        <section className="py-20 bg-gradient-to-b from-background to-secondary/5">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <Badge variant="outline" className="mb-4">Our Engineering Services</Badge>
              <h2 className="text-3xl lg:text-4xl font-bold mb-4">
                Comprehensive Design Engineering Solutions
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                From structural design to technical drafting, we provide the complete range of engineering services with precision and professional expertise.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {engineeringServices.map((service, index) => (
                <Card key={index} className="h-full hover:shadow-lg transition-shadow border-0 bg-white/60 backdrop-blur-sm">
                  <CardHeader>
                    <div className="flex items-center gap-4 mb-4">
                      <div className="p-3 rounded-full bg-primary/10">
                        <service.icon className="h-8 w-8 text-primary" />
                      </div>
                      <CardTitle className="text-xl">{service.title}</CardTitle>
                    </div>
                    <CardDescription className="text-base">
                      {service.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {service.services.map((item, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-sm">
                          <div className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0"></div>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Projects */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <Badge variant="outline" className="mb-4">Engineering Excellence</Badge>
              <h2 className="text-3xl lg:text-4xl font-bold mb-4">
                Professional Design Engineering Work
              </h2>
              <p className="text-xl text-muted-foreground">
                See our engineering expertise in action across various design and technical projects.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <Card className="group overflow-hidden border-0 shadow-lg">
                <div className="relative overflow-hidden">
                  <img 
                    src={designEngineeringTeam} 
                    alt="Professional design engineering team" 
                    className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 text-white">
                    <h3 className="font-semibold">Expert Engineering Team</h3>
                    <p className="text-sm text-white/80">Licensed P.Eng. professionals</p>
                  </div>
                </div>
              </Card>
              
              <Card className="group overflow-hidden border-0 shadow-lg">
                <div className="relative overflow-hidden">
                  <img 
                    src={engineeringBlueprints} 
                    alt="Technical engineering blueprints and drawings" 
                    className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 text-white">
                    <h3 className="font-semibold">Technical Drawings</h3>
                    <p className="text-sm text-white/80">Precision CAD work</p>
                  </div>
                </div>
              </Card>
              
              <Card className="group overflow-hidden border-0 shadow-lg">
                <div className="relative overflow-hidden">
                  <img 
                    src={engineeringConsultation} 
                    alt="Engineering consultation and planning" 
                    className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 text-white">
                    <h3 className="font-semibold">Design Consultation</h3>
                    <p className="text-sm text-white/80">Expert guidance</p>
                  </div>
                </div>
              </Card>
              
              <Card className="group overflow-hidden border-0 shadow-lg">
                <div className="relative overflow-hidden">
                  <img 
                    src={structuralModeling} 
                    alt="3D structural modeling and analysis" 
                    className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 text-white">
                    <h3 className="font-semibold">3D Modeling</h3>
                    <p className="text-sm text-white/80">Advanced BIM services</p>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </section>

        {/* Professional Engineering CTA */}
        <section className="py-16 bg-primary text-primary-foreground">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <div className="flex items-center justify-center gap-3 mb-4">
                <Award className="h-8 w-8" />
                <h2 className="text-3xl font-bold">Licensed Professional Engineers Ready to Help</h2>
              </div>
              <p className="text-xl mb-8 opacity-90">
                Need professional engineering design or technical consultation? Our licensed P.Eng. engineers are ready to provide expert solutions for your project.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" variant="secondary" className="text-lg px-8" asChild>
                  <Link to="/contact">Request Engineering Consultation</Link>
                </Button>
                <Button size="lg" variant="outline" className="text-lg px-8 bg-white/10 border-white/30 text-white hover:bg-white/20" asChild>
                  <a href="tel:+16477608854">Call P.Eng.: (647) 760-8854</a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-20 bg-gradient-to-b from-secondary/5 to-background">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <Badge variant="outline" className="mb-4">Why Choose Blue Knight Engineering</Badge>
              <h2 className="text-3xl lg:text-4xl font-bold mb-4">
                Ontario's Trusted Design Engineering Professionals
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Professional Engineers with decades of experience, advanced technology, and commitment to excellence in every design project.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <Card className="text-center border-0 bg-white/60 backdrop-blur-sm p-6">
                <div className="p-4 rounded-full bg-primary/10 w-fit mx-auto mb-4">
                  <Award className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Licensed P.Eng.</h3>
                <p className="text-muted-foreground">Professional Engineers licensed in Ontario with extensive design experience.</p>
              </Card>
              
              <Card className="text-center border-0 bg-white/60 backdrop-blur-sm p-6">
                <div className="p-4 rounded-full bg-primary/10 w-fit mx-auto mb-4">
                  <Monitor className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Advanced Technology</h3>
                <p className="text-muted-foreground">Latest CAD software and BIM technology for precision and efficiency.</p>
              </Card>
              
              <Card className="text-center border-0 bg-white/60 backdrop-blur-sm p-6">
                <div className="p-4 rounded-full bg-primary/10 w-fit mx-auto mb-4">
                  <CheckCircle className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Code Compliance</h3>
                <p className="text-muted-foreground">All designs meet or exceed Ontario Building Code and safety standards.</p>
              </Card>
              
              <Card className="text-center border-0 bg-white/60 backdrop-blur-sm p-6">
                <div className="p-4 rounded-full bg-primary/10 w-fit mx-auto mb-4">
                  <Clock className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Fast Delivery</h3>
                <p className="text-muted-foreground">Efficient project delivery without compromising quality or accuracy.</p>
              </Card>
            </div>
          </div>
        </section>

        {/* Service Areas */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <Badge variant="outline" className="mb-4">Service Coverage</Badge>
              <h2 className="text-3xl lg:text-4xl font-bold mb-4">
                Design Engineering Services Across Ontario
              </h2>
              <p className="text-xl text-muted-foreground">
                We provide professional engineering services throughout Ontario's major cities and communities.
              </p>
            </div>
            
            <div className="max-w-4xl mx-auto">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {serviceAreas.map((area, index) => (
                  <Card key={index} className="text-center p-4 hover:shadow-md transition-shadow border-0 bg-white/60 backdrop-blur-sm">
                    <div className="flex items-center justify-center gap-2">
                      <MapPin className="h-4 w-4 text-primary" />
                      <span className="font-medium">{area}</span>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-gradient-to-b from-background to-secondary/5">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <Badge variant="outline" className="mb-4">Frequently Asked Questions</Badge>
              <h2 className="text-3xl lg:text-4xl font-bold mb-4">
                Design Engineering Services FAQ
              </h2>
              <p className="text-xl text-muted-foreground">
                Common questions about our design engineering, CAD drafting, and technical consulting services.
              </p>
            </div>
            
            <div className="max-w-4xl mx-auto grid gap-6">
              {faqData.map((faq, index) => (
                <Card key={index} className="border-0 bg-white/60 backdrop-blur-sm">
                  <CardHeader>
                    <CardTitle className="text-left text-lg">{faq.question}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-20 bg-gradient-to-br from-primary via-primary-dark to-secondary text-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Ready to Start Your Engineering Project?
              </h2>
              <p className="text-xl mb-8 text-white/90">
                Get professional design engineering services from licensed P.Eng. engineers. Contact us for expert consultation and competitive rates.
              </p>
              
              <div className="grid md:grid-cols-3 gap-8 mb-12">
                <div className="text-center">
                  <div className="p-4 rounded-full bg-white/10 w-fit mx-auto mb-4">
                    <Phone className="h-8 w-8" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Call Us</h3>
                  <p className="text-white/80">
                    <a href="tel:+16477608854" className="hover:text-white transition-colors">
                      (647) 760-8854
                    </a>
                  </p>
                </div>
                
                <div className="text-center">
                  <div className="p-4 rounded-full bg-white/10 w-fit mx-auto mb-4">
                    <Mail className="h-8 w-8" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Email Us</h3>
                  <p className="text-white/80">
                    <a href="mailto:info@blueknightcontracting.ca" className="hover:text-white transition-colors">
                      info@blueknightcontracting.ca
                    </a>
                  </p>
                </div>
                
                <div className="text-center">
                  <div className="p-4 rounded-full bg-white/10 w-fit mx-auto mb-4">
                    <MapPin className="h-8 w-8" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Service Area</h3>
                  <p className="text-white/80">All of Ontario</p>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" variant="secondary" className="text-lg px-8" asChild>
                  <Link to="/contact">Get Engineering Quote</Link>
                </Button>
                <Button size="lg" variant="outline" className="text-lg px-8 bg-white/10 border-white/30 text-white hover:bg-white/20" asChild>
                  <Link to="/gallery">View Our Work</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </>
  );
};

export default DesignEngineeringServices;